import { NgClass } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '../../../../api/services/account.service';
import { map, switchMap, tap } from 'rxjs';
import { NgxMaskDirective } from 'ngx-mask';
import { AuthStore } from '../../../../api/auth/auth.store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent, ToastType } from '../../../../shared/components/layout/snackbar/snackbar.component';
import { VerifyType } from '../verify-email/verify-email.component';

function equalTo(key: string, confirmationKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const input = control.get(key);
    const confirmationInput = control.get(confirmationKey);

    if (input && confirmationInput && input.value !== confirmationInput.value) {
      confirmationInput.setErrors({ notEquivalent: true });
      return { notEquivalent: true };
    } else {
      return null;
    }
  };
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [MatFormFieldModule, ReactiveFormsModule, NgClass, MatInputModule, MatIconModule, NgxMaskDirective],
})
export class RegisterComponent implements OnInit {
  loading = signal(false);
  private inviteGuid?: string;
  form: FormGroup = new FormGroup({});
  otpForm: FormGroup = new FormGroup({});
  hide = true;
  chide = true;
  disabled = signal(false);
  regSuccessful = false;
  formOptions!: AbstractControlOptions;

  private authStore = inject(AuthStore);

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private accountService: AccountService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {
    const formOptions: AbstractControlOptions = {
      validators: [equalTo('password', 'confirmPassword')],
    };

    this.formOptions = formOptions;
  }

  ngOnInit() {
    this.form = this.fb.group(
      {
        email: this.fb.control({ value: null, disabled: true }, Validators.required),
        firstName: this.fb.control(null),
        lastName: this.fb.control(null),
        password: this.fb.control(null, Validators.required),
        confirmPassword: this.fb.control(null, Validators.required),
        inviteGuid: this.fb.control(null, Validators.required),
        externalIdentityNumber: this.fb.control(null),
      },
      this.formOptions,
    );

    this.loading.set(true);
    this.route.queryParams
      .pipe(
        map((params) => {
          const inviteGuid: string | null = params['invite'];
          if (inviteGuid) {
            this.form.get('inviteGuid')?.setValue(inviteGuid);
            return inviteGuid;
          } else {
            this.loading.set(false);
          }
          return null;
        }),
        switchMap((inviteGuid) => (inviteGuid ? this.accountService.getInviteDetails(inviteGuid!) : [])),
      )
      .subscribe((inviteDetails: any) => {
        if (inviteDetails) {
          this.form.get('email')?.setValue(inviteDetails.email);
          this.form.get('firstName')?.setValue(inviteDetails.firstName);
          this.form.get('lastName')?.setValue(inviteDetails.lastName);
        }

        this.loading.set(false);
      });
  }

  checkEmail(email: any, control: any) {}

  registerUser() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.disabled.set(true);

    const { email, password, externalIdentifications } = this.form.getRawValue();
    const formValues = {
      ...this.form.getRawValue(),
    };
    if (this.form.get('externalIdentityNumber')?.value) {
      formValues.externalIdentifications = [
        {
          typeId: 1, // TODO there is only one for now
          number: this.form.get('externalIdentityNumber')?.value,
        },
      ];
    }
    this.accountService.registerUserTotp(formValues).subscribe({
      next: () => {
        const email = this.form.get('email')?.value;
        const forgot = false;
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Success, message: `Successfully Registered` },
        });
        this.router.navigateByUrl('/verify-email', {
          state: { email, forgot, verifyType: VerifyType.VerifyEmail },
        });
        this.disabled.set(false);
      },
      error: (error) => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: `Error registering user` },
        });
        this.disabled.set(false);
      },
    });
  }

  async onSubmit2fa() {
    if (!this.otpForm.valid) {
      return;
    }

    this.disabled.set(true);
    try {
      const result = await this.authStore.login(
        this.otpForm.value.email,
        this.otpForm.value.password,
        this.otpForm.value.verificationCode,
      );

      if (result.success) {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Success, message: `Login successful` },
        });
        this.router.navigate(['/dashboard']);
      } else {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: `Invalid 2FA code` },
        });
      }
    } finally {
      this.disabled.set(false);
    }
  }
}
